import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms' | null;
}
export function LegalModal({
  isOpen,
  onClose,
  type
}: LegalModalProps) {
  const {
    t
  } = useLanguage();
  // Close on Escape key
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);
  const sections = type === 'privacy' ? t.legalModal.privacy : t.legalModal.terms;
  const title = type === 'privacy' ? t.legalModal.privacyTitle : t.legalModal.termsTitle;
  return <AnimatePresence data-id="element-346">
      {isOpen && type && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8" onClick={onClose} data-id="element-347">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" data-id="element-348" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} transition={{
        duration: 0.35,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative bg-riam-dark border border-riam-gold/30 rounded-sm max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl shadow-riam-gold/10" style={{
        boxShadow: '0 0 60px rgba(212, 168, 87, 0.15), 0 25px 80px rgba(0, 0, 0, 0.6)'
      }} data-id="element-349">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-riam-gold/20" data-id="element-350">
              <h3 className="gold-shine font-serif text-xl md:text-2xl" data-id="element-351">
                {title}
              </h3>
              <button type="button" onClick={onClose} aria-label={t.legalModal.closeLabel} className="text-riam-gold-light icon-glow hover:opacity-80 transition-opacity p-1" data-id="element-352">
                <X size={22} stroke="url(#riamGold)" data-id="element-353" />
              </button>
            </div>

            {/* Gold divider */}
            <div className="gold-divider w-full" data-id="element-354" />

            {/* Content */}
            <div className="overflow-y-auto px-6 md:px-8 py-6 max-h-[calc(85vh-130px)] text-riam-gold-light/85 font-sans text-sm leading-relaxed space-y-4" data-id="element-355">
              <p className="text-xs uppercase tracking-[0.3em] text-riam-gold-dark mb-4" data-id="element-356">
                {t.legalModal.lastUpdatedLabel}: {t.legalModal.lastUpdatedDate}
              </p>
              {sections.map((section, i) => <div key={i} className="space-y-2" data-id="element-357">
                  <h4 className="gold-shine-soft font-serif text-lg pt-2" data-id="element-358">
                    {section.heading}
                  </h4>
                  <p data-id="element-359">{section.body}</p>
                </div>)}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}